import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

function isTutorUser(user) {
  if (!user) return false;
  if (user.role === "admin" || (user.roles || []).includes("admin")) return false;
  return user.role === "tutor" || (user.roles || []).includes("tutor");
}

export function isTutorOrientationComplete(user) {
  return Boolean(user?.tutor_orientation_completed);
}

export function isTutorDetailsOnboardingComplete(user) {
  return Boolean(user?.tutor_onboarding_completed);
}

export function isTutorAccessComplete(user) {
  if (!isTutorUser(user)) return true;
  return isTutorOrientationComplete(user) && isTutorDetailsOnboardingComplete(user);
}

function isAllowlistedPath(pathname) {
  if (!pathname) return false;
  if (pathname.startsWith("/onboarding/tutor/platform")) return true;
  if (pathname.startsWith("/tutor-onboarding/")) return true;
  if (
    pathname === "/login"
    || pathname === "/register"
    || pathname === "/forgot-password"
    || pathname === "/verify-email"
  ) {
    return true;
  }
  if (
    pathname === "/"
    || pathname.startsWith("/find-a-tutor")
    || pathname.startsWith("/tutors/")
    || pathname.startsWith("/pricing")
    || pathname.startsWith("/how-it-works")
    || pathname.startsWith("/subjects")
    || pathname.startsWith("/tutor-tiers")
    || pathname.startsWith("/resources")
    || pathname.startsWith("/about")
    || pathname.startsWith("/faq")
    || pathname.startsWith("/contact")
    || pathname.startsWith("/legal/")
    || pathname.startsWith("/become-a-tutor")
    || pathname.startsWith("/tutor-interview")
  ) {
    return true;
  }
  return false;
}

function isBlockedPortalPath(pathname) {
  if (!pathname) return false;
  return (
    pathname.startsWith("/dashboard")
    || pathname.startsWith("/messages")
    || pathname.startsWith("/library")
    || pathname.startsWith("/book/")
  );
}

/**
 * Soft gate for tutors who have not finished Academy + details onboarding.
 * Blocks dashboard/messages/library and redirects into the missing step.
 */
export default function TutorAccessGate({ children }) {
  const { user, loading, refresh } = useAuth();
  const location = useLocation();
  const [resumeToken, setResumeToken] = useState(null);
  const [resolvingResume, setResolvingResume] = useState(false);

  const tutor = isTutorUser(user);
  const incomplete = tutor && !isTutorAccessComplete(user);
  const needsOrientation = tutor && !isTutorOrientationComplete(user);
  const needsDetails = tutor && isTutorOrientationComplete(user) && !isTutorDetailsOnboardingComplete(user);
  const shouldIntercept =
    incomplete
    && isBlockedPortalPath(location.pathname)
    && !isAllowlistedPath(location.pathname);

  useEffect(() => {
    if (!needsDetails || !shouldIntercept) return undefined;
    let cancelled = false;
    setResolvingResume(true);
    api
      .get("/tutor/applications/mine")
      .then(async ({ data }) => {
        if (cancelled) return;
        const status = data?.status;
        const token = data?.onboarding_token;
        if (status === "approved") {
          // Existing onboarded tutor missing the flag — force auth refresh for backfill.
          await refresh?.();
          setResumeToken(null);
          return;
        }
        if (token && status === "accepted") {
          setResumeToken(token);
          return;
        }
        setResumeToken(null);
      })
      .catch(() => {
        if (!cancelled) setResumeToken(null);
      })
      .finally(() => {
        if (!cancelled) setResolvingResume(false);
      });
    return () => {
      cancelled = true;
    };
  }, [needsDetails, shouldIntercept, refresh]);

  if (loading) return children;
  if (!shouldIntercept) return children;

  if (needsOrientation) {
    const next = encodeURIComponent(location.pathname + (location.search || ""));
    return <Navigate to={`/onboarding/tutor/platform?next=${next}`} replace />;
  }

  if (needsDetails) {
    if (resolvingResume) {
      return (
        <div className="min-h-[40vh] flex items-center justify-center text-sm text-aus-onyx/55">
          Continuing your tutor onboarding…
        </div>
      );
    }
    if (resumeToken) {
      return <Navigate to={`/tutor-onboarding/${resumeToken}`} replace />;
    }
    return <Navigate to="/onboarding/tutor/platform" replace />;
  }

  return children;
}
